import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * NALLI — Recently Viewed (PRD §4a).
 *
 * The section is a verbatim copy of the Product Slider, so the markup, CSS,
 * `_product-card` blocks and Swiper are all the theme's own — this script only
 * swaps in the right products:
 *
 *   1. records the current PDP view
 *   2. fetches the SAME section rendered in search context (so Liquid builds
 *      real slider slides for the viewed product ids)
 *   3. reorders the slides by recency + past-purchase bias, drops the current
 *      product, trims to the configured count
 *   4. swaps them into the live section and re-inits Swiper with the exact
 *      options the section's own inline script uses
 */

const SWIPER_OPTIONS = (sectionId, desktopColumns) => ({
  navigation: {
    nextEl: `.swiper-btn-prev-${sectionId}`,
    prevEl: `.swiper-btn-next-${sectionId}`,
  },
  pagination: {
    el: `.swiper-pagination-${sectionId}`,
    clickable: true,
  },
  breakpoints: {
    200: { slidesPerView: 2, spaceBetween: 8 },
    780: { slidesPerView: 3, spaceBetween: 10 },
    1024: { slidesPerView: desktopColumns, spaceBetween: 10 },
  },
});

async function renderRail(root) {
  const sectionId = root.dataset.rvSectionId;
  const currentId = root.dataset.rvCurrentId || null;
  const minItems = parseInt(root.dataset.rvMinItems || '3', 10);
  const limit = parseInt(root.dataset.rvCount || '10', 10);
  const searchUrl = root.dataset.rvSearchUrl || '/search';

  // Record this PDP view first (PRD: last 15, 30 days, deduped, newest first).
  if (currentId) RecentlyViewed.addProduct(currentId);

  const ids = RecentlyViewed.getProducts().filter((id) => id !== currentId);

  // PRD: hide the widget entirely below the threshold.
  if (!sectionId || ids.length < minItems) return;

  const recencyRank = new Map(ids.map((id, i) => [id, i]));
  const biasTypes = new Set(
    (root.dataset.rvBiasTypes || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean)
  );

  const url = new URL(searchUrl, location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('type', 'product');
  url.searchParams.set('section_id', sectionId);

  const response = await fetch(url);
  if (!response.ok) return;

  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const fetched = doc.querySelector(`#nalli-rv-${CSS.escape(sectionId)}`) || doc.querySelector('[data-rv-section-id]');
  if (!fetched) return;

  const track = fetched.querySelector('.swiper-wrapper');
  if (!track) return;

  const slides = Array.from(track.querySelectorAll('.swiper-slide')).filter((slide) => {
    const id = slide.dataset.productId;
    return id && id !== currentId && recencyRank.has(id);
  });

  if (slides.length < minItems) return;

  slides.sort((a, b) => {
    const biasA = biasTypes.has((a.dataset.productType || '').toLowerCase()) ? 0 : 1;
    const biasB = biasTypes.has((b.dataset.productType || '').toLowerCase()) ? 0 : 1;
    if (biasA !== biasB) return biasA - biasB;
    return (recencyRank.get(a.dataset.productId) ?? 0) - (recencyRank.get(b.dataset.productId) ?? 0);
  });

  track.replaceChildren(...slides.slice(0, limit));

  // Swap the theme's own markup in wholesale, then re-init its carousel.
  root.innerHTML = fetched.innerHTML;
  root.hidden = false;

  if (typeof window.Swiper === 'function') {
    const desktopColumns = parseInt(root.dataset.rvColumns || '4', 10);
    new window.Swiper(`.mySwiperProduct-${sectionId}`, SWIPER_OPTIONS(sectionId, desktopColumns));
  }
}

function init() {
  for (const root of document.querySelectorAll('[data-rv-section-id]')) {
    if (root.dataset.rvDone === 'true') continue;
    root.dataset.rvDone = 'true';
    renderRail(root).catch((error) => console.error('Recently viewed error:', error));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
